/* eslint no-mixed-operators:0 */

import { useState, useContext, useEffect } from 'react';
import {
  Button,
  Box,
  ButtonGroup,
  InputLabel,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  TextField
} from '@mui/material';

import {
  DeleteForever as DeleteForeverIcon,
  Add as AddIcon,
} from '@mui/icons-material';

import { Layout as DashboardLayout } from 'layouts/portal/layout';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import { Tracking } from 'components/Tracking';
import { Departments, availableCategoryGroups, AuthContext, EditCategory } from 'components';


export const CustomCategoriesView = (props) => {
  const { category } = props;
  const [ deleteModalOpen, setDeleteModalOpen ] = useState(false);
  const [ categoryToDelete, setCategoryToDelete ] = useState(null);
  const [ selectedDepartment, setSelectedDepartment ] = useState(availableCategoryGroups[0]);
  const [ newCategory, setNewCategory ] = useState("")
  const { customCategories, addCustomCategory, deleteCustomCategory, sortCustomCategory, notify } = useContext(AuthContext);
  const visibleEditCategories = Object.keys(customCategories).map((c) => ({
    ...customCategories[c],
    id: c
  })).filter((c) => c.category_group === selectedDepartment && c.category_id !== 'sections').sort((a, b) => a.sorting?.indexOf(a.id) > b.sorting?.indexOf(b.id) ? 1 : -1)

  useEffect(() => { Tracking.track('Portal - Categories') }, [])

  const handleAddCategory = () => {
    let current_categories = visibleEditCategories.map(c => c.category.toLowerCase().trim())

    if (current_categories.indexOf(newCategory.toLowerCase().trim()) !== -1) {
      notify({
        message: `Category ${newCategory} already exists`,
        variant: 'warning'
      })
    } else {
      if (newCategory) {
        const new_category = {
          displayName: newCategory
        }
        addCustomCategory(availableCategoryGroups.indexOf(selectedDepartment), new_category, () => {
          setNewCategory("")
          notify({
            message: `Category ${new_category.displayName} has been created`,
          })
        })
      }
    }

  }

  const handleDeleteModalOpen = (category) => {
    setCategoryToDelete(category)
    setDeleteModalOpen(true);
  }

  const handleDeleteModalClose = () => {
    setCategoryToDelete(null)
    setDeleteModalOpen(false);
  }

  const handleDeleteCategory = () => {
    deleteCustomCategory(availableCategoryGroups.indexOf(selectedDepartment), categoryToDelete.category_id, () => {
      handleDeleteModalClose()
      notify({
        message: `Category ${categoryToDelete.category} has been removed`,
      })
    })
  }

  const handleDragEnd = (r) => {
    sortCustomCategory(availableCategoryGroups.indexOf(selectedDepartment), r.draggableId, r.source.index, r.destination.index)
  }

  return (
    <DashboardLayout>
      <Typography variant="h2">Categories</Typography>
      <Typography variant="body1">Drag categories to change the order</Typography>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 2
        }}
      >
        <Departments onChange={setSelectedDepartment} />
        <DragDropContext
          onDragEnd={handleDragEnd}
        >
          <Droppable droppableId={`${selectedDepartment}-add-custom-category`}>
            {provided => (
              <List 
                sx={{ maxWidth: 400 }}
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                <ListItem
                  sx={{ pl: 0 }}
                  key={`${selectedDepartment}-add-custom-category`}
                  secondaryAction={
                    <IconButton disabled={!newCategory} onClick={handleAddCategory} color="primary" edge="end" aria-label="Add custom category">
                      <AddIcon />
                    </IconButton>
                  }
                >
                  <ListItemButton role={undefined} dense>
                    <TextField 
                      InputProps={{
                        style: { 
                          minHeight: 'auto',
                        }
                      }}
                      sx={{
                        flexGrow: 1
                      }}
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      onKeyDown={e => { if(e.key === 'Enter') { handleAddCategory() } }}
                      size="small"
                      label="Add Category"
                      variant="standard" 
                    />
                  </ListItemButton>
                </ListItem>
                {visibleEditCategories.map((option, index) => {
                  return (
                    <Draggable
                      draggableId={option.category_id}
                      index={index}
                      key={`${option.category_id}`}
                    >
                      {provided_children => (
                        <ListItem
                          sx={{ pl: 0 }}
                          secondaryAction={
                            <>
                              <EditCategory title={option.category || customCategories[option]?.category} category={option.category_id} department={option.category_group_id} />
                              <IconButton onClick={() => { handleDeleteModalOpen(option) }} edge="end" aria-label="Delete custom category">
                                <DeleteForeverIcon />
                              </IconButton>
                            </>
                          }
                          {...provided_children.draggableProps}
                          {...provided_children.dragHandleProps}
                          ref={provided_children.innerRef}
                        >
                          <ListItemButton role={undefined} dense>
                            <ListItemText primary={option.category || customCategories[option]?.category} />
                          </ListItemButton>
                        </ListItem>
                      )}
                    </Draggable>
                  )
                })}
                <Dialog
                  open={deleteModalOpen}
                  onClose={handleDeleteModalClose}
                >
                  <DialogTitle>
                    Delete Category?
                  </DialogTitle>
                  <DialogContent>
                    <DialogContentText sx={{ textAlign: 'center' }}>
                      <span>Tap the Delete button to delete this category. This does not delete any products.</span>
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleDeleteModalClose}>Cancel</Button>
                    <Button color="error" onClick={handleDeleteCategory} autoFocus>Delete</Button>
                  </DialogActions>
                </Dialog>
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    </DashboardLayout>
  );
}
